<template lang="pug">
.send-message-reply-table-component.d-flex.flex-column.align-items-stretch
  // テーブル
  b-table(
    :items="_value"
    :fields="_fields"
    :busy="isLoading"
    :filter="filter"
    :filter-function="filterTable"
    sticky-header
    no-border-collapse
    table-class="message-table send-message-reply-table"
    show-empty
  )
    // ローディング
    template(#table-busy)
      .text-center.table-loading-message
        b-spinner.spinner
        span.message.align-self-center 読込み中...
    // レコード無し
    template(#empty)
      .empty-message
        span 情報がありません
    // フィルター後レコード無し
    template(#emptyfiltered)
      .empty-message
        span 情報がありません

    // ヘッダー：返信リスト
    template(#head(message)="data")
      .d-flex.justify-content-center
        div(v-if="isLoading") 返信リスト
        div(v-else) 返信リスト（未読：{{ _unreadCount }}件）
      .d-flex.justify-content-end.align-items-center
        b-form-checkbox.only-unread-check(v-model="filter.onlyUnread" :value="true" :unchecked-value="false") 未読のみ表示
        b-input-group.search-filter.mr-1(size="sm")
          b-input-group-prepend
            b-input-group-text.search-filter-icon
              .search-icon
          b-form-input.search-filter-input(v-model="filter.searchWord" type="search" placeholder="検索ワードを入力してください")
        HelpIcon.d-flex.justify-content-end.align-items-center(popoverId="send-message-reply-table-search-filter-help" title="検索項目について")
          template(v-slot:text)
            div 検索項目は以下になります。
            .family(v-if="type == 0")
              div ・利用者名
              div ・利用者名（ふりがな）
              div ・部屋名
              div ・家族ユーザー名
            .family(v-if="type == 1")
              div ・種類名
              div ・施設関係者グループ名
              div ・施設関係者メンバー名

    // セル：返信リスト
    template(#cell(message)="row")
      .message.d-flex.flex-row(@click="onMessage(row.item)" :class="getReadClass(row.item)")
        .unread-bar
        .contents.d-flex.flex-row.align-items-center
          .d-flex.flex-row.align-items-center.flex-grow-1(v-if="row.item.type == 'error'")
            .name
              span.error-badge.badge.badge-pill.badge-danger !
              span {{ row.item.mainName }}（{{ row.item.subName }}）
          .d-flex.flex-row.align-items-center.flex-grow-1(v-else)
            .name
              span {{ row.item.mainName }}（{{ row.item.subName }}）
          .date.d-flex.flex-row.align-items-center.justify-content-end
            span.datetime.mr-2 {{ convertDate(row.item) }}
            button.btn.reply-btn.mr-2(type="button") 詳細／返信
            ContactConfirmSwitch(:value="row.item.readed" @onSwitch="onReadSwitch(row.item)")
</template>

<script lang="ts">
import { Component, Prop, Vue } from "vue-property-decorator";
import HelpIcon from "@/pc/basics/HelpIcon.vue";
import ContactConfirmSwitch from "@/pc/basics/ContactConfirmSwitch.vue";

/**
 * ### メッセージ送信詳細の返信リストコンポーネント
 */
@Component({
  components: {
    HelpIcon,
    ContactConfirmSwitch
  }
})
export default class SendMessageReplyListTable extends Vue {
  /** 1行あたりの情報配列 */
  @Prop({ required: true })
  value!: Array<SendMessageReplyListTableItem>;

  /** 列名配列 */
  @Prop({ required: true })
  fields!: Array<SendMessageReplyListTableField>;

  /** ローディング表示可否 */
  @Prop({ required: true })
  isLoading!: boolean;

  /** 種別(家族：0、施設関係者：1) */
  @Prop({ required: true })
  type!: number;

  /** フィルタリング条件 */
  filter: FilterSetting = { onlyUnread: false, searchWord: "" };
  /** フィルター選択項目 */
  filterOptions: Array<{ value: string; text: string }> = [];
  /** 未読のみチェック */
  onlyUnread: boolean = false;
  /** 検索文字列 */
  searchWord: string = "";

  get _unreadCount(): number {
    let count: number = 0;
    for (const message of this.value) {
      if (message.readed == false) {
        count++;
      }
    }
    return count;
  }

  get _value(): Array<SendMessageReplyListTableItem> {
    return this.value;
  }

  get _fields(): Array<SendMessageReplyListTableField> {
    return this.fields;
  }

  public onSearchFilter(): void {
    this.filter.searchWord = this.searchWord;
    return;
  }

  /**
   * ### フィルタリング
   */
  public filterTable(row: SendMessageReplyListTableItem, filter: FilterSetting): boolean {
    return this.filterOnlyUnread(row, filter) && this.filterSearchWords(row, filter);
  }

  public filterOnlyUnread(row: SendMessageReplyListTableItem, filter: FilterSetting): boolean {
    if (filter.onlyUnread == false) {
      return true;
    }
    if (filter.onlyUnread == true) {
      if (row.readed == false) {
        return true;
      }
    }
    return false;
  }

  /**
   * ### 検索ワードのチェック結果を返す
   *
   * - チェック対象はフィルタキー1～3
   */
  public filterSearchWords(row: SendMessageReplyListTableItem, filter: FilterSetting): boolean {
    if (filter.searchWord == "") {
      return true;
    }

    const words: string[] = this.$filterUtil.createSearchWord(filter.searchWord);

    let foundFilterKey1: boolean = true;
    for (const word of words) {
      if (row.filterKey1.indexOf(word) == -1) {
        foundFilterKey1 = false;
      }
    }

    let foundFilterKey2: boolean = true;
    for (const word of words) {
      if (row.filterKey2.indexOf(word) == -1) {
        foundFilterKey2 = false;
      }
    }

    let foundFilterKey3: boolean = true;
    for (const word of words) {
      if (row.filterKey3.indexOf(word) == -1) {
        foundFilterKey3 = false;
      }
    }

    return foundFilterKey1 || foundFilterKey2 || foundFilterKey3;
  }

  /**
   * ### メッセージクリックイベント
   *
   * - 未読であれば未読を既読に変更し、親コンポーネントに既読にしたことを通知する
   * - 返信リストの未読件数を減算する
   */
  public onMessage(item: SendMessageReplyListTableItem): void {
    this.$emit("onMessage", item);
    return;
  }

  /**
   * ### 返信メッセージの未読/既読用のCssクラスを返す
   */
  public getReadClass(item: SendMessageReplyListTableItem): string {
    if (item.readed == false) {
      if (item.type == SendMessageReplyListTableType.REPLY) {
        return "unread";
      } else if (item.type == SendMessageReplyListTableType.ERROR) {
        return "error-unread";
      }
    }
    return "";
  }

  /**
   * ### 数値の日付を文字列に変換する
   */
  public convertDate(item: SendMessageReplyListTableItem): string {
    return this.$dateUtil.convertNumberToString(item.datetime, "yyyy/mm/dd hh:MM");
  }

  public onReadSwitch(item: SendMessageReplyListTableItem): void {
    this.$emit("onReadSwitch", item);
    return;
  }
}

interface FilterSetting {
  onlyUnread: boolean;
  searchWord: string;
}

/** テーブルの列名の情報 */
export interface SendMessageReplyListTableField {
  key: string;
  label: string;
  sortable: boolean;
  sortDirection: string;
  class?: string;
}

/** テーブルの1行あたりの情報 */
export interface SendMessageReplyListTableItem {
  parentMessagesSortKey: string;
  residentId?: number;
  familyId?: number;
  groupId?: number;
  memberId?: number;
  mainName: string;
  subName: string;
  datetime: number;
  readed: boolean;
  type: SendMessageReplyListTableType;
  isDeleted?: boolean;
  messages: Array<any>;
  filterKey1: string;
  filterKey2: string;
  filterKey3: string;
}

export enum SendMessageReplyListTableType {
  REPLY = "reply",
  ERROR = "error"
}
</script>

<style lang="scss" scoped>
@import "../../../styles/pc/_colors.scss";
@import "../../../styles/pc/_mixin.scss";

.send-message-reply-table-component {
  border-top: solid 1px $border-darkgray;
  border-right: solid 1px $border-darkgray;
  border-bottom: solid 1px $border-darkgray;

  height: calc(100vh - 245px);

  // 返信メッセージ
  .message {
    padding: 3px 4px 3px 8px;
    min-height: 30px;

    &:hover {
      background-color: $list-hover !important;
    }

    .only-unread-check {
      min-width: 126px;
      padding: 0 !important;
      font-weight: normal !important;
    }

    .search-filter {
      width: 260px;
      min-width: 260px;

      .search-filter-icon {
        background-color: #fff !important;
        .search-icon {
          @include svg-icon("../../../assets/search.svg", $font-base-color, 20px, 20px);
        }
      }
      .search-filter-input {
        border-left: 0;
      }
    }

    .reply-btn {
      height: 24px !important;
      font-size: 12px !important;
    }

    // 未読
    .unread-bar {
      margin-right: 16px;
    }

    .contents {
      width: 100%;
      .name {
        max-width: 400px;
        // 最大横幅を超えたら省略記号で表示する
        text-overflow: ellipsis;
        white-space: nowrap;
        overflow: hidden;
      }
      .date {
        min-width: 320px;
        max-width: 320px;
        font-size: 14px;
      }
    }

    // 未読の返信メッセージを強調するスタイル
    .unread {
      // リンクであることが分かるようにカーソルを変える
      cursor: pointer;
      .unread-bar {
        display: block;
        width: 4px;
        margin-right: 12px;
        background-color: $font-key-color;
      }
      .contents {
        .name,
        .date {
          color: $font-key-color;
          font-weight: bold;
        }
      }
    }
    // 未読の返信メッセージ(エラー)を強調するスタイル
    .error-unread {
      // リンクであることが分かるようにカーソルを変える
      cursor: pointer;
      .unread-bar {
        display: block;
        width: 4px;
        margin-right: 12px;
        background-color: $errormessage-font-color;
      }
      .contents {
        .name,
        .date {
          color: $errormessage-font-color;
          font-weight: bold;
        }
      }
    }
    .error-badge {
      margin: 0 8px 2px 0;
    }
  }
}
</style>
